import { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { NetWorthSeriesRow } from "../../lib/api-modules/types.js";
import { formatCentsCompact } from "../../lib/util/money.js";

/**
 * Net-worth time-series chart. Phase 4 (single line) → Phase 16d
 * (multi-series + per-account stacked-area + reconstructed-shading) →
 * Phase 16e (`valueField` toggle: net worth vs positions-only portfolio).
 *
 * Recharts is the project-standard charting library for non-trading
 * views; lightweight-charts stays reserved for OHLC candlesticks.
 *
 * **Three modes** (mutually exclusive):
 *
 *   - **`mode === "line"`** (default): renders the household's primary
 *     value series as a gradient-filled area. When `overlays` are
 *     present, EVERY series is normalized to its starting value = 100
 *     (chart-start normalization, per Phase 16d open-call #1) and the
 *     Y-axis formatter switches from dollars to × start. Each overlay
 *     renders as a separate `<Line>` in its assigned color; the
 *     primary line stays the gradient-filled area.
 *
 *   - **`mode === "stacked-area"`**: per-account breakdown mode.
 *     `accountBreakdown` supplies the account ordering + color; each
 *     account renders as a stacked `<Area>` with `stackId="acct"`.
 *     Overlays are ignored in this mode (lines don't compose with
 *     stacks). Y-axis stays in dollars.
 *
 * **`valueField`** (Phase 16e): switches the primary value series
 * between `net_worth_cents` (default — assets minus liabilities) and
 * `portfolio_value_cents` (positions-only, no cash, no margin, no
 * liabilities). The portfolio-mode answers "did the equity portion
 * actually beat SPY?" honestly — net worth visually drags below SPY
 * when cash gets spent, even if the equity portion is fine. The
 * stacked-area mode swaps `account_breakdown` ↔ `portfolio_by_account`
 * to match.
 *
 * **Reconstructed-shading**: when `highlightReconstructed` is on,
 * `<ReferenceArea>` covers the backfilled date range (rows where
 * `source === "backfill"`) at low opacity. The boundary is the LAST
 * backfill row in the series (chronological); after that, snapshots
 * are live. This communicates the data-fidelity caveat visually
 * without a banner.
 *
 * Privacy mode masks Y-axis ticks + tooltip values to `••••`.
 */

export type NetWorthChartMode = "line" | "stacked-area";
export type NetWorthChartValueField =
  | "net_worth_cents"
  | "portfolio_value_cents";

export interface NetWorthChartOverlay {
  /** Stable key for React + dataKey. e.g. "SPY". */
  readonly key: string;
  /** Display label for the tooltip + legend. e.g. "SPY". */
  readonly label: string;
  /** Color for the rendered Line. From the chart-theme overlay palette. */
  readonly color: string;
  /** Daily bars in close-cents. Sparse OK; the chart aligns by date. */
  readonly bars: ReadonlyArray<{ readonly date: string; readonly close_cents: number }>;
}

export interface NetWorthChartAccount {
  readonly account_id: string;
  readonly name: string;
  readonly color: string;
}

interface NetWorthChartProps {
  readonly snapshots: readonly NetWorthSeriesRow[];
  readonly hideValues: boolean;
  readonly mode?: NetWorthChartMode;
  readonly overlays?: readonly NetWorthChartOverlay[];
  readonly accountBreakdown?: readonly NetWorthChartAccount[];
  readonly highlightReconstructed?: boolean;
  /**
   * Phase 16e (2026-05-10): which snapshot field feeds the primary
   * value series. `net_worth_cents` (default) = assets − liabilities.
   * `portfolio_value_cents` = positions only (no cash, no margin, no
   * liabilities). Stacked-area mode reads the matching breakdown
   * (`account_breakdown` vs `portfolio_by_account`) automatically.
   */
  readonly valueField?: NetWorthChartValueField;
}

interface ChartPoint {
  readonly date: string;
  readonly source?: "backfill" | "scheduled";
  readonly value_cents: number;
  /** Normalized to start = 100 when overlays are present. */
  readonly portfolio_normalized?: number;
  /** Per-overlay normalized values, keyed by overlay.key. */
  readonly [overlayKey: string]: number | string | undefined;
}

interface StackedPoint {
  readonly date: string;
  readonly source?: "backfill" | "scheduled";
  readonly [accountId: string]: number | string | undefined;
}

export function NetWorthChart({
  snapshots,
  hideValues,
  mode = "line",
  overlays,
  accountBreakdown,
  highlightReconstructed,
  valueField = "net_worth_cents",
}: NetWorthChartProps) {
  const isStacked = mode === "stacked-area" && accountBreakdown && accountBreakdown.length > 0;
  const hasOverlays = !isStacked && overlays && overlays.length > 0;
  const isPortfolio = valueField === "portfolio_value_cents";
  const seriesLabel = isPortfolio ? "Portfolio value" : "Net worth";

  const readValue = (s: NetWorthSeriesRow): number => {
    if (isPortfolio) return s.portfolio_value_cents ?? 0;
    return s.net_worth_cents;
  };

  const lineData = useMemo<ChartPoint[]>(() => {
    if (snapshots.length === 0) return [];
    if (!hasOverlays) {
      return snapshots.map((s) => {
        const point: ChartPoint = {
          date: s.date,
          value_cents: readValue(s),
        };
        if (s.source !== undefined) {
          (point as { source?: "backfill" | "scheduled" }).source = s.source;
        }
        return point;
      });
    }
    // Normalize every series to start = 100. Build a date → overlay-close
    // map per overlay first.
    const overlaysByKey = new Map<string, Map<string, number>>();
    const overlayStartByKey = new Map<string, number>();
    for (const ov of overlays) {
      const byDate = new Map<string, number>();
      for (const bar of ov.bars) byDate.set(bar.date, bar.close_cents);
      overlaysByKey.set(ov.key, byDate);
      // Pick the first overlay close that's on-or-after the first
      // snapshot date as the basis. Fallback: first bar.
      const firstSnapDate = snapshots[0]?.date;
      let basis: number | undefined;
      if (firstSnapDate) {
        for (const bar of ov.bars) {
          if (bar.date >= firstSnapDate) {
            basis = bar.close_cents;
            break;
          }
        }
      }
      basis ??= ov.bars[0]?.close_cents;
      if (basis !== undefined && basis > 0) {
        overlayStartByKey.set(ov.key, basis);
      }
    }
    const firstSnap = snapshots[0];
    const portfolioStart = firstSnap ? readValue(firstSnap) : 0;
    return snapshots.map((s) => {
      const value = readValue(s);
      const point: ChartPoint = {
        date: s.date,
        value_cents: value,
        portfolio_normalized:
          portfolioStart > 0 ? (value / portfolioStart) * 100 : 100,
      };
      if (s.source !== undefined) {
        (point as { source?: "backfill" | "scheduled" }).source = s.source;
      }
      for (const ov of overlays) {
        const closeCents = overlaysByKey.get(ov.key)?.get(s.date);
        const basis = overlayStartByKey.get(ov.key);
        if (closeCents !== undefined && basis !== undefined && basis > 0) {
          (point as Record<string, number | string | undefined>)[ov.key] =
            (closeCents / basis) * 100;
        }
      }
      return point;
    });
    // readValue closes over `isPortfolio`, which is derived from valueField.
  }, [snapshots, overlays, hasOverlays, valueField]);

  const stackedData = useMemo<StackedPoint[]>(() => {
    if (!isStacked) return [];
    return snapshots.map((s) => {
      const point: StackedPoint = { date: s.date };
      if (s.source !== undefined) {
        (point as { source?: "backfill" | "scheduled" }).source = s.source;
      }
      // Phase 16e: stacked-area reads the matching breakdown for the
      // selected valueField — `portfolio_by_account` for positions-only,
      // `account_breakdown` for net worth (existing behavior).
      const breakdown =
        (isPortfolio ? s.portfolio_by_account : s.account_breakdown) ?? {};
      for (const acct of accountBreakdown ?? []) {
        // Negative values (legacy data, margin) clamp to 0 so the
        // stacked area doesn't render below baseline.
        const v = breakdown[acct.account_id];
        (point as Record<string, number | string | undefined>)[acct.account_id] =
          typeof v === "number" && v > 0 ? v : 0;
      }
      return point;
    });
  }, [snapshots, accountBreakdown, isStacked, isPortfolio]);

  // Reconstructed-shading bounds: first + last date where source === "backfill".
  const reconstructedBounds = useMemo(() => {
    if (!highlightReconstructed) return null;
    let first: string | undefined;
    let last: string | undefined;
    for (const s of snapshots) {
      if (s.source === "backfill") {
        first ??= s.date;
        last = s.date;
      }
    }
    return first && last ? { first, last } : null;
  }, [snapshots, highlightReconstructed]);

  if (snapshots.length === 0) {
    return (
      <div
        className="rounded-md border border-dashed border-[var(--color-border-default)] p-8 text-center text-sm text-[var(--color-muted)]"
        data-testid="networth-chart-empty"
      >
        No snapshots yet — the first one runs nightly at 7pm ET.
      </div>
    );
  }

  return (
    <div className="h-[260px] w-full" data-testid="networth-chart">
      <ResponsiveContainer width="100%" height="100%" minWidth={0}>
        <AreaChart
          data={(isStacked ? stackedData : lineData)}
          margin={{ top: 16, right: 16, bottom: 8, left: 0 }}
        >
          <defs>
            <linearGradient id="netWorthFill" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor="var(--color-accent)"
                stopOpacity={0.35}
              />
              <stop
                offset="100%"
                stopColor="var(--color-accent)"
                stopOpacity={0.02}
              />
            </linearGradient>
            {/* Diagonal stripe pattern for the reconstructed-region shade. */}
            <pattern
              id="reconstructedStripes"
              width="6"
              height="6"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" fill="transparent" />
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="6"
                stroke="var(--color-muted)"
                strokeOpacity="0.18"
                strokeWidth="2"
              />
            </pattern>
          </defs>
          <CartesianGrid strokeOpacity={0.1} vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.7 }}
            tickFormatter={shortDate}
            minTickGap={24}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.7 }}
            tickFormatter={(v: number) => {
              if (hideValues) return "••••";
              if (hasOverlays) return `${v.toFixed(0)}`;
              return formatCentsCompact(v);
            }}
            width={hasOverlays ? 40 : 64}
            {...(hasOverlays ? { domain: ["auto", "auto"] as const } : {})}
          />
          <Tooltip
            content={
              hideValues
                ? () => null
                : ({ active, payload, label }) => {
                    if (!active || !payload || payload.length === 0) return null;
                    const dataPoint = payload[0]?.payload as
                      | (ChartPoint & StackedPoint)
                      | undefined;
                    return (
                      <div className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-2 text-xs shadow-[var(--shadow-card)]">
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-medium text-[var(--color-text)]">
                            {label}
                          </span>
                          {dataPoint?.source !== undefined && (
                            <SourcePill source={dataPoint.source} />
                          )}
                        </div>
                        <ul className="mt-1 space-y-0.5">
                          {payload.map((entry, idx) => {
                            const value = entry.value;
                            if (typeof value !== "number") return null;
                            const isPrimary =
                              entry.dataKey === "value_cents" ||
                              entry.dataKey === "portfolio_normalized";
                            const formatted = hasOverlays
                              ? `${value.toFixed(2)}`
                              : formatCentsCompact(value);
                            return (
                              <li
                                key={`${String(entry.dataKey)}-${idx}`}
                                className="flex items-center justify-between gap-4"
                              >
                                <span
                                  className="flex items-center gap-1.5"
                                  style={{ color: entry.color }}
                                >
                                  <span
                                    className="inline-block h-2 w-2 rounded-full"
                                    style={{ background: entry.color }}
                                  />
                                  <span className="text-[var(--color-text)]">
                                    {isPrimary ? seriesLabel : entry.name}
                                  </span>
                                </span>
                                <span
                                  className="font-mono tabular-nums"
                                  style={{ color: entry.color }}
                                >
                                  {formatted}
                                  {hasOverlays && " ×"}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                        {dataPoint?.source === "backfill" && (
                          <p className="mt-2 max-w-[260px] text-[10px] leading-snug text-[var(--color-muted)]">
                            Reconstructed from currently-held lots × historical
                            Tiingo prices. Lots sold before this date aren't
                            included. Cash, margin, and liabilities are
                            zero-projected for backfilled rows.
                          </p>
                        )}
                      </div>
                    );
                  }
            }
          />

          {reconstructedBounds && (
            <ReferenceArea
              x1={reconstructedBounds.first}
              x2={reconstructedBounds.last}
              fill="url(#reconstructedStripes)"
              fillOpacity={1}
              ifOverflow="extendDomain"
            />
          )}

          {isStacked
            ? (accountBreakdown ?? []).map((acct) => (
                <Area
                  key={acct.account_id}
                  type="monotone"
                  dataKey={acct.account_id}
                  name={acct.name}
                  stackId="acct"
                  stroke={acct.color}
                  fill={acct.color}
                  fillOpacity={0.55}
                  strokeWidth={1}
                  isAnimationActive={false}
                />
              ))
            : hasOverlays
              ? (
                  <>
                    <Area
                      type="monotone"
                      dataKey="portfolio_normalized"
                      name={seriesLabel}
                      stroke="var(--color-accent)"
                      strokeWidth={2}
                      fill="url(#netWorthFill)"
                      isAnimationActive={false}
                    />
                    {(overlays ?? []).map((ov) => (
                      <Line
                        key={ov.key}
                        type="monotone"
                        dataKey={ov.key}
                        name={ov.label}
                        stroke={ov.color}
                        strokeWidth={1.5}
                        dot={false}
                        isAnimationActive={false}
                      />
                    ))}
                  </>
                )
              : (
                  <Area
                    type="monotone"
                    dataKey="value_cents"
                    name={seriesLabel}
                    stroke="var(--color-accent)"
                    strokeWidth={2}
                    fill="url(#netWorthFill)"
                    isAnimationActive={false}
                  />
                )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function SourcePill({ source }: { source: "backfill" | "scheduled" }) {
  if (source === "backfill") {
    return (
      <span className="rounded-full bg-[var(--color-elevated)] px-2 py-0.5 text-[10px] font-medium text-[var(--color-muted)]">
        Reconstructed
      </span>
    );
  }
  return (
    <span className="rounded-full bg-[var(--color-accent-soft)] px-2 py-0.5 text-[10px] font-medium text-[var(--color-accent)]">
      Live snapshot
    </span>
  );
}

function shortDate(iso: string): string {
  const [y, m] = iso.split("-");
  if (!y || !m) return iso;
  const month = MONTHS[Number(m) - 1] ?? m;
  return `${month} '${y.slice(2)}`;
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;
